import { Providers } from "@microsoft/mgt-react";
import axios from "axios";
import { ServiceEndpoints } from "./config";
import { v4 as uuidv4 } from 'uuid';
import { NetworkHealthService } from "./services/NetworkHealth";

/**
 * Handle API errors and detect VPN-related issues.
 * This function checks if the error is VPN-related and reports it to NetworkHealthService.
 * @param error The error from the API call
 * @throws Re-throws the original error after processing
 */
function handleApiError(error: unknown): never {
    const healthService = NetworkHealthService.getInstance();
    
    // Report the failure - this will trigger VPN lock if it's a VPN-related error
    healthService.reportApiFailure(error);
    
    // Re-throw the original error so the caller can handle it
    throw error;
}

/**
 * Report successful API call to NetworkHealthService
 */
function handleApiSuccess<T>(response: T): T {
    const healthService = NetworkHealthService.getInstance();
    healthService.reportApiSuccess();
    return response;
}

export class axiosWrapper {
    public static async Get(Url: string) {
        const correlationId = uuidv4();
        
        try {
            const token = await Providers.globalProvider.getAccessTokenForScopes(ServiceEndpoints.caseAPIEndpoint.scopes);
            
            const response = await axios.get(ServiceEndpoints.caseAPIEndpoint.endpoint + Url, {
                headers: {
                    'correlationId': correlationId,
                    'Authorization': `Bearer ${token}`
                },
            });
            
            return handleApiSuccess(response);
        } catch (error) {
            handleApiError(error);
        }
    }

    public static async Post(Url: string, data?: any) {
        console.log("axiosWrapper.Post called with URL:", Url, "data:", data);
        
        const correlationId = uuidv4();
        console.log("Generated correlationId:", correlationId);
        
        try {
            console.log("Getting access token for scopes:", ServiceEndpoints.caseAPIEndpoint.scopes);
            const token = await Providers.globalProvider.getAccessTokenForScopes(ServiceEndpoints.caseAPIEndpoint.scopes);
            console.log("Access token obtained successfully");
            
            const fullUrl = ServiceEndpoints.caseAPIEndpoint.endpoint + Url;
            console.log("Making POST request to full URL:", fullUrl);
            
            const result = await axios.post(fullUrl, data, {
                headers: {
                    'correlationId': correlationId,
                    'Authorization': `Bearer ${token}`
                },
            });
            
            console.log("POST request completed successfully:", result);
            return handleApiSuccess(result);
        } catch (error) {
            console.error("Error in axiosWrapper.Post:", error);
            handleApiError(error);
        }
    }

    public static async Delete(Url: string) {
        const correlationId = uuidv4();
        
        try {
            const token = await Providers.globalProvider.getAccessTokenForScopes(ServiceEndpoints.caseAPIEndpoint.scopes);
            
            const response = await axios.delete(ServiceEndpoints.caseAPIEndpoint.endpoint + Url, {
                headers: {
                    'correlationId': correlationId,
                    'Authorization': `Bearer ${token}`
                },
            });
            
            return handleApiSuccess(response);
        } catch (error) {
            handleApiError(error);
        }
    }

    public static async Patch(Url: string, data?: any) {
        const correlationId = uuidv4();
        
        try {
            const token = await Providers.globalProvider.getAccessTokenForScopes(ServiceEndpoints.caseAPIEndpoint.scopes);
            
            const response = await axios.patch(ServiceEndpoints.caseAPIEndpoint.endpoint + Url, JSON.stringify(data), {
                headers: {
                    'Content-Type': 'application/json',
                    'correlationId': correlationId,
                    'Authorization': `Bearer ${token}`
                },
            });
            
            return handleApiSuccess(response);
        } catch (error) {
            handleApiError(error);
        }
    }

    public static async GetValidate(Url: string, isCustomerFlow: boolean) {
        const correlationId = uuidv4();
        
        try {
            const token = await Providers.globalProvider.getAccessTokenForScopes(ServiceEndpoints.caseAPIEndpoint.scopes);
            
            const response = await axios.get(ServiceEndpoints.caseAPIEndpoint.endpoint + Url, {
                headers: {
                    'correlationId': correlationId,
                    'isCustomerFlow': isCustomerFlow,
                    'Authorization': `Bearer ${token}`
                },
            });
            
            return handleApiSuccess(response);
        } catch (error) {
            handleApiError(error);
        }
    }

    public static async GetDocument(Url: string) {
        const correlationId = uuidv4();
        
        try {
            const token = await Providers.globalProvider.getAccessTokenForScopes(ServiceEndpoints.caseAPIEndpoint.scopes);
            
            const response = await axios.get(ServiceEndpoints.caseAPIEndpoint.endpoint + Url, {
                headers: {
                    'correlationId': correlationId,
                    'Content-Type': 'application/pdf',
                    'Authorization': `Bearer ${token}`
                },
                responseType: 'arraybuffer',
                timeout: 30000
            });
            
            return handleApiSuccess(response);
        } catch (error) {
            handleApiError(error);
        }
    }

    public static async GetDocumentBlob(Url: string) {
        const correlationId = uuidv4();
        
        try {
            const token = await Providers.globalProvider.getAccessTokenForScopes(ServiceEndpoints.caseAPIEndpoint.scopes);
            
            const response = await axios.get(ServiceEndpoints.caseAPIEndpoint.endpoint + Url, {
                headers: {
                    'correlationId': correlationId,
                    'Content-Type': 'application/pdf',
                    'Authorization': `Bearer ${token}`
                },
                responseType: 'blob',
                timeout: 30000
            });
            
            return handleApiSuccess(response);
        } catch (error) {
            handleApiError(error);
        }
    }

    public static async UploadDocument(Url: string, data?: any) {
        const correlationId = uuidv4();
        
        try {
            const token = await Providers.globalProvider.getAccessTokenForScopes(ServiceEndpoints.caseAPIEndpoint.scopes);
            
            const response = await axios.post(ServiceEndpoints.caseAPIEndpoint.endpoint + Url, data, {
                headers: {
                    'correlationId': correlationId,
                    'Authorization': `Bearer ${token}`
                },
                timeout: 30000
            });
            
            return handleApiSuccess(response);
        } catch (error) {
            handleApiError(error);
        }
    }

    public static async Put(Url: string, data?: any) {
        const correlationId = uuidv4();
        
        try {
            const token = await Providers.globalProvider.getAccessTokenForScopes(ServiceEndpoints.caseAPIEndpoint.scopes);
            
            const response = await axios.put(ServiceEndpoints.caseAPIEndpoint.endpoint + Url, data, {
                headers: {
                    'correlationId': correlationId,
                    'Authorization': `Bearer ${token}`
                },
            });
            
            return handleApiSuccess(response);
        } catch (error) {
            handleApiError(error);
        }
    }
}